<script setup>
import { ref } from "vue";
import { useField, useForm } from 'vee-validate';
import { CONTRAGENT_SCHEME } from './scheme';
import { toTypedSchema } from '@vee-validate/zod';
import { 
    Input as AInput, 
    FormItem as AFormItem, 
    DatePicker as ADatePicker, 
    Select as ASelect, 
    SelectOption as ASelectOption,
    Radio,
    Form,
    Divider
} from 'ant-design-vue';
import { useToast } from "vue-toastification";
import axios from "axios";
import { useRouter } from "vue-router";

const props = defineProps({
    // Встроенный режим: форма открыта в модалке поверх другой формы — ИИН приходит
    // готовым, а после сохранения управление возвращается вызвавшему экрану.
    embedded: { type: Boolean, default: false },
    iin: { type: String, default: "" },
});

const emit = defineEmits(["created"]);

const keepValuesOnUnmount = ref(true);

const { handleSubmit, meta } = useForm({
    keepValuesOnUnmount: keepValuesOnUnmount,
    validationSchema: toTypedSchema(CONTRAGENT_SCHEME)
});

const {value: iin, errorMessage: iinErrorMessage} = useField("iin", undefined, {
    initialValue: props.iin
});
const {value: name, errorMessage: nameErrorMessage} = useField("name");
const {value: surname, errorMessage: surnameErrorMessage} = useField("surname");
const {value: patronymic, errorMessage: patronymicErrorMessage} = useField("patronymic");
const {value: date_of_birth, errorMessage: date_of_birthErrorMessage} = useField("date_of_birth");
const {value: sex, errorMessage: sexErrorMessage} = useField("sex");
const {value: doc_type, errorMessage: doc_typeErrorMessage} = useField("doc_type");
const {value: doc_number, errorMessage: doc_numberErrorMessage} = useField("doc_number");
const {value: phone, errorMessage: phoneErrorMessage} = useField("phone");
const {value: email, errorMessage: emailErrorMessage} = useField("email");

const toast = useToast();
const router = useRouter();

const onSubmit = handleSubmit(async (values) => {
    try {
        await axios.post("/V1/road-accidents/set-subject", values);
        toast.success("Контрагент успешно создан");

        if (props.embedded) {
            emit("created", values.iin);
            return;
        }

        router.push("/excuse");
    } catch (error) {
        toast.error(error.response?.data?.message || "Не удалось создать контрагента")
    }
});

const handleChange = (value) => {
    const date = value.format('YYYY-MM-DD');
    date_of_birth.value = date;
}
</script>

<template>
    <Form 
        class="contragent-form" 
        @submit="onSubmit" 
        layout="vertical"
    >
        <h2 class="contragent-form__title">Данные контрагента</h2>

        <div class="inputs">
            <AFormItem 
                label="ИИН"
                :validate-status="iinErrorMessage"
                :help="iinErrorMessage ? iinErrorMessage : ''"
            >
                <AInput 
                    v-model:value="iin"
                    v-maska="'XXXXXXXXXXXX'"
                    :disabled="embedded && !!props.iin"
                    placeholder="ИИН"
                    class="questionnaire_card_input"
                    :class="iinErrorMessage ? 'error' : ''"
                    name="iin"
                />
            </AFormItem>

            <AFormItem 
                label="Имя"
                :validate-status="nameErrorMessage"
                :help="nameErrorMessage ? nameErrorMessage : ''"
            >
                <AInput 
                    v-model:value="name"
                    placeholder="Имя заявителя"
                    class="questionnaire_card_input"
                    :class="nameErrorMessage ? 'error' : ''"
                    name="name"
                />
            </AFormItem>
            
            <AFormItem 
                label="Фамилия заявителя"
                :validate-status="surnameErrorMessage"
                :help="surnameErrorMessage ? surnameErrorMessage : ''"
            >
                <AInput 
                    v-model:value="surname"
                    placeholder="Фамилия"
                    class="questionnaire_card_input"
                    :class="surnameErrorMessage ? 'error' : ''"
                    name="surname"
                />
            </AFormItem>

            <AFormItem 
                label="Отчество заявителя"
                :validate-status="patronymicErrorMessage"
                :help="patronymicErrorMessage ? patronymicErrorMessage : ''"
            >
                <AInput 
                    v-model:value="patronymic"
                    placeholder="Отчество"
                    class="questionnaire_card_input"
                    :class="patronymicErrorMessage ? 'error' : ''"
                    name="patronymic"
                />
            </AFormItem>

            <AFormItem
                label="Дата рождения"
                :validate-status="date_of_birthErrorMessage"
                :help="date_of_birthErrorMessage ? date_of_birthErrorMessage : ''"
            >
                <ADatePicker
                    placeholder="Дата рождения"
                    class="questionnaire_card_input"
                    :class="date_of_birthErrorMessage ? 'error' : ''"
                    format="DD.MM.YYYY"
                    name="date_of_birth"
                    @change="handleChange"
                />
            </AFormItem>

            <AFormItem 
                label="Тип документа"
                :validate-status="doc_typeErrorMessage"
                :help="doc_typeErrorMessage ? doc_typeErrorMessage : ''"
            >
                <ASelect
                    v-model:value="doc_type"
                    placeholder="Выберите тип документа"
                    class="questionnaire_card_input doc_type_select"
                    :class="doc_typeErrorMessage ? 'error' : ''"
                    name="doc_type"
                >
                    <ASelectOption
                        :value="1165"
                        label="Удостоверение"
                        class="questionnaire_card_input"
                    >
                        Удостоверение
                    </ASelectOption>
                </ASelect>
            </AFormItem>

            <AFormItem 
                label="Номер документа"
                :validate-status="doc_numberErrorMessage"
                :help="doc_numberErrorMessage ? doc_numberErrorMessage : ''"
                class="doc_number"
            >
                    <AInput
                        v-model:value="doc_number"
                        placeholder="Номер документа"
                        class="questionnaire_card_input"
                        :class="doc_numberErrorMessage ? 'error' : ''"
                        name="doc_number"
                    />
            </AFormItem>

            <AFormItem
                label="Пол"
                :validate-status="sexErrorMessage"
                :help="sexErrorMessage ? sexErrorMessage : ''"
            >
                <Radio.Group
                    block
                    v-model:value="sex"
                    :class="sexErrorMessage ? 'error' : ''"
                    name="sex"
                >
                    <Radio
                        value="М"
                    >
                        Мужской
                    </Radio>
                    <Radio
                        value="Ж"
                    >
                        Женский
                    </Radio>
                </Radio.Group>
            </AFormItem>

            <div/>

            <AFormItem
                label="Номер телефона"
                :validate-status="phoneErrorMessage"
                :help="phoneErrorMessage ? phoneErrorMessage : ''"
            >
                <AInput
                    :placeholder="'+7 (000) 000 00 00'"
                    class="questionnaire_card_input"
                    v-maska="'+7 (XXX) XXX XX XX'"
                    v-model:value="phone"
                    :class="phoneErrorMessage ? 'error' : ''"
                    name="phone"
                />
            </AFormItem>

            <AFormItem
                label="E-mail"
                :validate-status="emailErrorMessage"
                :help="emailErrorMessage ? emailErrorMessage : ''"
            >
                <AInput
                    :placeholder="'hello@mail.kz'"
                    class="questionnaire_card_input"
                    v-model:value="email"
                    :class="emailErrorMessage ? 'error' : ''"
                    name="email"
                />
            </AFormItem>
        </div>

        <Divider/>

        <div class="submit-button-wrapper">
            <button class="submit-button" :disabled="!meta.valid">
                Сохранить
            </button>
        </div>
    </Form>
</template>

<style>
.contragent-form {
    margin-top: 50px;
}

.contragent-form .inputs {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    column-gap: 20px;

    margin-top: 30px;
}

.contragent-form__title {
    font-size: 24px;
    font-weight: 500;
    color: #373737;

    margin: 0;
}

.contragent-form .error {
    border: 1px solid #F5010A;
}

.doc_type_select {
    .ant-select-selector {
        background-color: transparent !important;
        border: none !important;
        height: 55px !important;
    }

    .ant-select-selection-placeholder, .ant-select-selection-item {
        display: flex;
        align-items: center;
    }
}

.doc_number {
    grid-column: span 2;
}

.submit-button-wrapper {
    width: 100%;
    display: flex;
    justify-content: flex-end;
}

.submit-button {
    width: 320px;
    background-color: #007BFF;
    color: white;

    padding: 12px 0;

    border-radius: 8px;

    border: none;
    cursor: pointer;

    margin-top: 20px;

    &:not(:disabled):hover {
        background-image: linear-gradient(to right, #F5010A 0%, #1521E9 100%)
    }

    &:disabled {
        background-color: #3c9afd;
        cursor: not-allowed;
    }
}
</style>